import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WaitlistStrip — email capture on the root PortalChoice page.
 * Founding Members · First Generation.
 */
export default function WaitlistStrip() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [position, setPosition] = useState(null);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setStatus("error");
      setError("Please enter a valid email.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/waitlist/join`, { email: value, source: "portal-home" });
      if (data?.success) {
        setPosition(data.position ?? null);
        setStatus(data.already_joined ? "already" : "success");
      } else {
        setStatus("error");
        setError(data?.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus("error");
      setError(typeof detail === "string" ? detail : "We couldn't add you right now — please try again.");
    }
  }

  return (
    <div
      className="relative w-full max-w-2xl rounded-3xl px-6 sm:px-8 py-6 sm:py-7 border border-amber-300/28 bg-gradient-to-br from-[rgba(238,216,154,0.10)] via-[rgba(20,26,55,0.55)] to-[rgba(6,12,28,0.60)] backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
      data-testid="waitlist-strip"
    >
      {/* Amber glow */}
      <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" aria-hidden />

      {status === "success" || status === "already" ? (
        // ── Success state ────────────────────────────────────────────
        <div className="text-center relative" data-testid="waitlist-success">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/12 border border-emerald-400/30 text-emerald-200 text-[10.5px] uppercase tracking-[0.24em] mb-4">
            <Check size={13} /> {status === "already" ? "Already on the list" : "You're in"}
          </div>
          <div className="font-heading font-light text-white text-2xl sm:text-3xl leading-tight tracking-tight">
            {position ? (
              <>You are Founding Member <span className="italic font-medium text-amber-200">#{position}</span></>
            ) : (
              <>Welcome, Founding Member.</>
            )}
          </div>
          <p className="mt-3 font-body text-zinc-300/90 text-[13.5px] leading-relaxed max-w-md mx-auto">
            {status === "already"
              ? "This email is already on our waitlist. We'll be in touch the moment there's something meaningful to share."
              : "We just sent a welcome to your inbox. When the app opens, your founding-member premium access is on us."}
          </p>
        </div>
      ) : (
        // ── Capture state ────────────────────────────────────────────
        <div className="relative">
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-300/12 border border-amber-300/30 text-amber-200 text-[10px] uppercase tracking-[0.28em]">
              <Sparkles size={11} /> Founding Members · First Generation
            </div>
            <div className="mt-4 font-heading font-light text-white text-2xl sm:text-3xl leading-tight tracking-tight">
              Join the practice before the app <span className="italic font-medium">opens.</span>
            </div>
            <p className="mt-3 font-body text-zinc-300/85 text-[13.5px] sm:text-sm leading-relaxed max-w-lg mx-auto">
              Founding members receive complimentary access to premium features at launch,
              early previews, and an opportunity to help shape the first generation of My Mood Groove.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row items-stretch gap-2.5" data-testid="waitlist-form">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@yourdomain.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
              disabled={status === "loading"}
              className="flex-1 rounded-full bg-white/[0.05] border border-white/15 focus:border-amber-300/60 focus:bg-white/[0.08] focus:outline-none text-white placeholder:text-zinc-500 text-sm px-5 py-3 transition"
              data-testid="waitlist-email-input"
              aria-label="Email address"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm tracking-tight shadow-[0_8px_24px_rgba(238,216,154,0.28)] hover:brightness-105 transition disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="waitlist-submit"
            >
              {status === "loading" ? <><Loader2 size={14} className="animate-spin" /> Joining…</> : "Become a founding member"}
            </button>
          </form>

          {status === "error" && (
            <div className="mt-3 text-center text-[12px] text-rose-300" data-testid="waitlist-error">{error}</div>
          )}

          <div className="mt-3 text-center text-[10.5px] text-zinc-500 tracking-wide">
            Private. No spam. Unsubscribe anytime.
          </div>
        </div>
      )}
    </div>
  );
}
